import { useEffect } from 'react';
import { toast } from 'sonner';

import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { billingUpgradePath } from '@/core/billing/upgrade-focus';
import {
  resolveUpgradeNotice,
  useEntitlementLabels,
} from '@/core/components/billing/upgrade-surfaces';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useUpgradeRequiredStore } from '@/core/store/upgrade-required.store';

const UPGRADE_TOAST_ID = 'upgrade-required';

/** 403 plan-lock → persistent toast with billing CTA (no dismissible sheet). */
export function UpgradeRequiredProvider() {
  const open = useUpgradeRequiredStore((state) => state.open);
  const context = useUpgradeRequiredStore((state) => state.context);
  const dismiss = useUpgradeRequiredStore((state) => state.dismiss);
  const clearContext = useUpgradeRequiredStore((state) => state.clearContext);
  const dict = useDictionary();
  const { plan } = useEntitlements();
  const { getFeatureLabel, getPlanLabel } = useEntitlementLabels();
  const { navigateShell } = useNavigateShell();

  useEffect(() => {
    if (!open || !context) return;

    const notice = resolveUpgradeNotice({
      upgradeDict: dict.entitlements.upgrade,
      feature: context.type === 'feature' ? context.feature : undefined,
      context,
      plan,
      getFeatureLabel,
      getPlanLabel,
    });

    const showPurchaseCta = canPurchaseBillingInApp();

    toast.warning(notice.title, {
      id: UPGRADE_TOAST_ID,
      description: showPurchaseCta
        ? notice.description
        : dict.workspace.billing.nativeWebOnlyNotice,
      duration: 8_000,
      ...(showPurchaseCta
        ? {
            action: {
              label: notice.cta,
              onClick: () => {
                navigateShell(
                  billingUpgradePath(
                    context.type === 'feature' ? context.feature : null,
                  ),
                );
              },
            },
          }
        : {}),
    });

    dismiss();
    clearContext();
  }, [
    open,
    context,
    dict.entitlements.upgrade,
    dict.workspace.billing.nativeWebOnlyNotice,
    plan,
    getFeatureLabel,
    getPlanLabel,
    navigateShell,
    dismiss,
    clearContext,
  ]);

  return null;
}
